import React, { useState, useRef, useEffect } from 'react';
import {
  ListBulletIcon,
  LinkIcon,
  CodeBracketIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface RichNoteEditorProps {
  initialContent?: string;
  initialTitle?: string;
  initialCategory?: string;
  initialTags?: string[];
  initialColor?: string;
  categories?: string[];
  onSave: (title: string, content: string, plainText: string, category: string, tags: string[], color: string) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const RichNoteEditor: React.FC<RichNoteEditorProps> = ({
  initialContent = '',
  initialTitle = '',
  initialCategory = 'Általános',
  initialTags = [],
  initialColor = 'blue',
  categories = [],
  onSave,
  onCancel,
  isEditing = false,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [category, setCategory] = useState(initialCategory);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [color, setColor] = useState(initialColor);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editorRef.current && initialContent) {
      editorRef.current.innerHTML = initialContent;
    }
    if (!isEditing && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleSave = () => {
    if (!title.trim()) {
      alert('Kérlek adj meg egy címet a jegyzetnek!');
      return;
    }

    const htmlContent = editorRef.current?.innerHTML || '';
    const plainText = editorRef.current?.innerText || '';
    
    onSave(title, htmlContent, plainText, category, tags, color);
  };

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const colors = [
    { name: 'Kék', value: 'blue', class: 'bg-blue-500' },
    { name: 'Zöld', value: 'green', class: 'bg-green-500' },
    { name: 'Lila', value: 'purple', class: 'bg-purple-500' },
    { name: 'Narancs', value: 'orange', class: 'bg-orange-500' },
    { name: 'Piros', value: 'red', class: 'bg-red-500' },
    { name: 'Rózsaszín', value: 'pink', class: 'bg-pink-500' },
    { name: 'Sárga', value: 'yellow', class: 'bg-yellow-500' },
    { name: 'Szürke', value: 'gray', class: 'bg-gray-500' },
  ];

  const insertLink = () => {
    const url = prompt('Add meg a link URL-jét:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl/Cmd + S to save
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    // Ctrl/Cmd + B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      execCommand('bold');
    }
    // Ctrl/Cmd + I for italic
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      execCommand('italic');
    }
    // Ctrl/Cmd + U for underline
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
      e.preventDefault();
      execCommand('underline');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Jegyzet szerkesztése' : 'Új jegyzet'}
          </h2>
          <button
            onClick={onCancel}
            className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Title Input */}
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
          <input
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Jegyzet címe..."
            className="w-full text-xl sm:text-2xl font-bold bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>

        {/* Category, Tags and Color */}
        <div className="px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kategória
              </label>
              {categories.length > 0 ? (
                <div>
                  {!isNewCategory ? (
                    <div className="relative">
                      <select
                        value={category}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setIsNewCategory(true);
                            setCategory('');
                          } else {
                            setCategory(e.target.value);
                            setIsNewCategory(false);
                          }
                        }}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Válassz kategóriát...</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                        <option value="__new__">+ Új kategória</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="Új kategória neve..."
                        className="flex-1 px-3 py-1.5 text-sm border border-blue-300 dark:border-blue-600 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsNewCategory(false);
                          setCategory('');
                        }}
                        className="px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm"
                        title="Mégse"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="pl. Munka, Projekt..."
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Címkék
              </label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Címke hozzáadása..."
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                >
                  +
                </button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-blue-900 dark:hover:text-blue-100"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Color */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Szín
              </label>
              <div className="flex gap-1.5">
                {colors.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className={`w-7 h-7 rounded-full ${c.class} ${
                      color === c.value
                        ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-gray-100'
                        : 'hover:scale-110'
                    } transition-all`}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-0.5 sm:gap-1 p-1.5 sm:p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 overflow-x-auto">
          <button
            onClick={() => execCommand('bold')}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors font-bold"
            title="Félkövér (Ctrl+B)"
          >
            <span className="text-sm sm:text-base">B</span>
          </button>
          <button
            onClick={() => execCommand('italic')}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors italic"
            title="Dőlt (Ctrl+I)"
          >
            <span className="text-sm sm:text-base">I</span>
          </button>
          <button
            onClick={() => execCommand('underline')}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors underline"
            title="Aláhúzott (Ctrl+U)"
          >
            <span className="text-sm sm:text-base">U</span>
          </button>
          
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          
          <button
            onClick={() => execCommand('insertUnorderedList')}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="Felsorolás"
          >
            <ListBulletIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={() => execCommand('insertOrderedList')}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="Számozott lista"
          >
            <span className="text-sm sm:text-base">1.</span>
          </button>
          
          <div className="w-px h-4 sm:h-6 bg-gray-300 dark:bg-gray-600 mx-0.5 sm:mx-1" />
          
          <button
            onClick={insertLink}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="Link beszúrása"
          >
            <LinkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={() => execCommand('formatBlock', 'pre')}
            className="hidden sm:block p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="Kód blokk"
          >
            <CodeBracketIcon className="w-5 h-5" />
          </button>

          <div className="w-px h-4 sm:h-6 bg-gray-300 dark:bg-gray-600 mx-0.5 sm:mx-1" />

          <select
            onChange={(e) => execCommand('formatBlock', e.target.value)}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            defaultValue=""
          >
            <option value="">Normál</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
          </select>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <div
            ref={editorRef}
            contentEditable
            onKeyDown={handleKeyDown}
            className="min-h-[200px] sm:min-h-[300px] outline-none text-gray-900 dark:text-gray-100 prose prose-sm sm:prose-base dark:prose-invert max-w-none
              [&>h1]:text-3xl [&>h1]:font-bold [&>h1]:mb-4 [&>h1]:mt-6
              [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:mt-5
              [&>h3]:text-xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:mt-4
              [&>p]:mb-3 [&>p]:leading-relaxed
              [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-3
              [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-3
              [&>li]:mb-1
              [&>pre]:bg-gray-100 [&>pre]:dark:bg-gray-900 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:mb-3 [&>pre]:overflow-x-auto
              [&>a]:text-blue-600 [&>a]:dark:text-blue-400 [&>a]:underline
              [&>blockquote]:border-l-4 [&>blockquote]:border-gray-300 [&>blockquote]:dark:border-gray-600 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:mb-3"
            data-placeholder="Kezdj el írni..."
          />
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
            💡 Tipp: Használd a <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+S</kbd> billentyűt a mentéshez
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={onCancel}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm sm:text-base"
            >
              Mégse
            </button>
            <button
              onClick={handleSave}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-sm text-sm sm:text-base"
            >
              {isEditing ? 'Mentés' : 'Létrehozás'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RichNoteEditor;
